import React, { useEffect, useMemo, useState } from "react";
import { Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import api, { formatMoney, formatNumber } from "../lib/api";
import { InfoD } from "./InfoD";
import { ExportButtons } from "../lib/exports";
import { col } from "../lib/exportCols";

const kfmt = (v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v);
const fecha = (d) => new Date(d).toLocaleDateString("es-ES", { month: "short", year: "numeric" });

function FamilyChart({ fam, points }) {
  const data = points.map((p) => ({ fecha: fecha(p.fecha), name: p.name, valor: fam.valores[p.study_id] ?? null }));
  return (
    <div className="mt-4 p-4 rounded-lg border border-slate-200 bg-slate-50" data-testid="inv-family-chart">
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Evolución del stock · {fam.familia}</div>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
          <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={kfmt} />
          <Tooltip formatter={(v) => formatMoney(v)} labelFormatter={(l, p) => p?.[0]?.payload?.name || l} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
          <Line type="monotone" dataKey="valor" name="Valor stock" stroke="#2563EB" strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function FamiliesTable({ data }) {
  const [sel, setSel] = useState(null);
  const pts = data.points;
  const first = pts[0], last = pts[pts.length - 1];
  const fam = data.families.find((f) => f.familia === sel);
  const expRows = data.families.map((f) => ({ familia: f.familia, ...Object.fromEntries(pts.map((p) => [p.study_id, f.valores[p.study_id]])),
    variacion: f.valores[first.study_id] != null && f.valores[last.study_id] != null ? f.valores[last.study_id] - f.valores[first.study_id] : null }));
  const expCols = [col.text("Familia", "familia"), ...pts.map((p) => col.eur(fecha(p.fecha), p.study_id)), ...(pts.length > 1 ? [col.eur("Variación", "variacion")] : [])];
  return (
    <div className="mt-6" data-testid="inv-families">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Evolución por familias · pulsa una familia para ver su gráfico</div>
        <ExportButtons filename="evolucion-inventario-familias" title="Evolución del inventario por familias" sheetName="Familias"
                       rows={expRows} columns={expCols} testIdPrefix="inv-families-export" />
      </div>
      {fam && <FamilyChart fam={fam} points={pts} />}
      <div className="overflow-auto max-h-[420px] mt-3 border border-slate-200 rounded-lg">
        <table className="dense" data-testid="inv-families-table">
          <thead><tr><th>Familia</th>{pts.map((p) => <th key={p.study_id} className="num" title={p.name}>{fecha(p.fecha)}</th>)}
            {pts.length > 1 && <th className="num">Variación</th>}</tr></thead>
          <tbody>
            {data.families.map((f) => {
              const a = f.valores[first.study_id], b = f.valores[last.study_id];
              const d = a != null && b != null ? b - a : null;
              return (
                <tr key={f.familia} onClick={() => setSel(f.familia === sel ? null : f.familia)} data-testid={`inv-family-row-${f.familia}`}
                    className={"cursor-pointer hover:bg-blue-50 " + (f.familia === sel ? "bg-blue-50" : "")}>
                  <td className="font-semibold">{f.familia}</td>
                  {pts.map((p) => <td key={p.study_id} className="num">{f.valores[p.study_id] != null ? formatMoney(f.valores[p.study_id]) : "—"}</td>)}
                  {pts.length > 1 && <td className={"num font-bold " + (d == null ? "text-slate-400" : d > 0 ? "text-red-600" : "text-emerald-700")}>
                    {d == null ? "—" : `${d > 0 ? "+" : ""}${formatMoney(d)}`}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function InventarioEvolucion() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/inventory/evolution").then((r) => setData(r.data)).catch(() => setData({ points: [], families: [] })); }, []);
  const chart = useMemo(() => (data?.points || []).map((p) => ({ ...p, fecha_l: fecha(p.fecha) })), [data]);

  if (!data) return <div className="card p-6 mb-8 text-slate-500"><Loader2 size={14} className="animate-spin inline mr-2" />Cargando evolución del inventario…</div>;
  if (!data.points.length) return null;
  const last = data.points[data.points.length - 1];

  return (
    <div className="card p-6 mb-8" data-testid="inv-evolution">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Inventario · un punto por estudio subido</div>
          <h3 className="font-display text-xl font-bold mt-1">Evolución del inventario</h3>
        </div>
        <div className="flex gap-6 text-right">
          <div><div className="kpi-label">Stock actual</div><div className="font-display text-xl font-extrabold" data-testid="inv-last-stock">{formatMoney(last.stock_eur)}</div></div>
          <div><div className="kpi-label flex items-center gap-1 justify-end">Más de 1 año <InfoD term="anio" /></div><div className="font-display text-xl font-extrabold text-amber-600" data-testid="inv-last-1y">{formatMoney(last.stock_1y_eur)}</div>
            <div className="text-[11px] text-slate-500">{formatNumber(last.stock_1y_refs)} refs.</div></div>
          <div><div className="kpi-label flex items-center gap-1 justify-end">D real <InfoD term="real" /></div><div className="font-display text-xl font-extrabold text-red-600" data-testid="inv-last-dreal">{formatMoney(last.d_real_eur)}</div>
            <div className="text-[11px] text-slate-500">{formatNumber(last.d_real_refs)} refs.</div></div>
        </div>
      </div>
      <div className="cursor-pointer" onClick={() => setOpen((o) => !o)} data-testid="inv-evolution-chart" role="button" tabIndex={0}>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={chart}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="fecha_l" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={kfmt} />
            <Tooltip formatter={(v) => formatMoney(v)} labelFormatter={(l, p) => p?.[0]?.payload?.name ? `${p[0].payload.name} · ${l}` : l} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="stock_eur" name="Stock total" stroke="#0F172A" strokeWidth={2.5} dot={{ r: 4 }} />
            <Line type="monotone" dataKey="stock_1y_eur" name="Stock con entrada de más de 1 año" stroke="#F59E0B" strokeWidth={2.5} dot={{ r: 4 }} />
            <Line type="monotone" dataKey="d_real_eur" name="De ellos sin ventas (D real)" stroke="#DC2626" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} />
            <Line type="monotone" dataKey="comprado_mes_sin_venta_eur" name="Comprado último mes sin ventas" stroke="#7C3AED" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <button onClick={() => setOpen((o) => !o)} className="mt-2 text-xs font-semibold text-blue-700 flex items-center gap-1 hover:underline" data-testid="inv-toggle-families">
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {open ? "Ocultar desglose por familias" : "Pulsa el gráfico para ver la evolución por familias"}
      </button>
      {open && <FamiliesTable data={data} />}
    </div>
  );
}
